import { useState, useEffect } from "react";
import Button from "@ingka/button";
import chevronDoubleLeft from "@ingka/ssr-icon/paths/chevron-double-left-small";
import chevronLeft from "@ingka/ssr-icon/paths/chevron-left-small";
import chevronRight from "@ingka/ssr-icon/paths/chevron-right-small";
import chevronDoubleRight from "@ingka/ssr-icon/paths/chevron-double-right-small";
import styles from "./Pagination.module.css";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 20, 50];
const SPINNER_DELAY_MS = 500;

interface PaginationProps {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: number[];
    loading?: boolean;
}

export function Pagination({ currentPage, totalItems, pageSize, onPageChange, onPageSizeChange, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, loading }: PaginationProps) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);

    const [showSpinner, setShowSpinner] = useState(false);
    useEffect(() => {
        if (!loading) { setShowSpinner(false); return; }
        const timer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
        return () => clearTimeout(timer);
    }, [loading]);

    if (totalItems <= pageSize && !onPageSizeChange) return null;

    return (
        <div className={styles.pagination}>
            <span className={styles.info}>
                {start}–{end} of {totalItems}
                {showSpinner && <span className={styles.spinner} />}
            </span>
            <div className={styles.controls}>
                {onPageSizeChange && (
                    <select
                        className={styles.sizeSelect}
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        title="Items per page"
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                )}
                {totalItems > pageSize && (
                    <div className={styles.buttons}>
                        <Button type="tertiary" size="xsmall" iconOnly ssrIcon={chevronDoubleLeft} onClick={() => onPageChange(1)} disabled={currentPage <= 1} title="First page" />
                        <Button type="tertiary" size="xsmall" iconOnly ssrIcon={chevronLeft} onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} title="Previous page" />
                        <Button type="tertiary" size="xsmall" iconOnly ssrIcon={chevronRight} onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} title="Next page" />
                        <Button type="tertiary" size="xsmall" iconOnly ssrIcon={chevronDoubleRight} onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} title="Last page" />
                    </div>
                )}
            </div>
        </div>
    );
}
